import { useState, useEffect, useRef } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Flower, ArrowRight, GoogleLogo } from "@phosphor-icons/react";
import { useAuth } from "@/lib/auth";
import { api, setToken } from "@/lib/api";
import usePageMeta from "@/lib/usePageMeta";

export default function Auth() {
  const [params] = useSearchParams();
  const [mode, setMode] = useState(params.get("mode") === "register" ? "register" : "login");
  usePageMeta({
    title: mode === "register" ? "Join the circle" : "Sign in",
    description: mode === "register"
      ? "Create your anonymous Wingwomen profile and find the women who've walked your path."
      : "Sign in to Wingwomen to reconnect with your circle.",
  });
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [agreed, setAgreed] = useState(false);
  const [adult, setAdult] = useState(false);
  const [busy, setBusy] = useState(false);
  const [processingGoogle, setProcessingGoogle] = useState(false);
  const { login, register, refresh } = useAuth();
  const nav = useNavigate();
  const processed = useRef(false);

  // Handle Google Sign-in callback (URL hash contains session_id after auth.emergentagent.com redirect)
  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  useEffect(() => {
    const hash = window.location.hash || "";
    if (!hash.includes("session_id=") || processed.current) return;
    processed.current = true;
    const sid = new URLSearchParams(hash.slice(1)).get("session_id");
    if (!sid) return;
    setProcessingGoogle(true);
    (async () => {
      try {
        const { data } = await api.post("/auth/google", { session_id: sid });
        setToken(data.token);
        await refresh();
        // clear the hash so a refresh doesn't retry
        window.history.replaceState({}, document.title, window.location.pathname);
        toast.success("Signed in with Google.");
        nav(data.user.onboarded ? "/app" : "/onboarding");
      } catch (err) {
        toast.error(err?.response?.data?.detail || "Google sign-in failed");
        setProcessingGoogle(false);
      }
    })();
  }, [nav, refresh]);

  const submit = async (e) => {
    e.preventDefault();
    if (mode === "register") {
      if (!adult) { toast.error("You must be 18 or older to join Wingwomen."); return; }
      if (!agreed) { toast.error("Please agree to the Terms and Medical Disclaimer to continue."); return; }
    }
    setBusy(true);
    try {
      const user = mode === "login" ? await login(email, password) : await register(email, password);
      toast.success(mode === "login" ? "Welcome back." : "Your circle awaits.");
      nav(user.onboarded ? "/app" : "/onboarding");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Something went wrong");
    }
    setBusy(false);
  };

  return (
    <div className="min-h-screen grid md:grid-cols-2 bg-cream">
      {processingGoogle && (
        <div className="fixed inset-0 z-50 bg-cream/90 backdrop-blur-sm grid place-items-center" data-testid="google-processing">
          <div className="text-center">
            <div className="w-10 h-10 mx-auto rounded-full border-2 border-coral/30 border-t-coral animate-spin" />
            <div className="mt-4 font-display text-lg">Signing you in…</div>
          </div>
        </div>
      )}
      <div className="hidden md:block relative">
        <img src="https://images.unsplash.com/photo-1633169621790-71e519cfb42d?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzl8MHwxfHNlYXJjaHwxfHxzb2Z0JTIwYm90YW5pY2FsJTIwc2FnZSUyMGxlYWYlMjBzaGFkb3d8ZW58MHx8fHwxNzg0MDY2OTY1fDA&ixlib=rb-4.1.0&q=85"
             alt="Botanical" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-ink/30" />
        <div className="relative h-full p-12 flex flex-col justify-between text-cream">
          <Link to="/" data-testid="auth-brand" className="flex items-center gap-2">
            <Flower size={26} weight="duotone" className="text-coral-700" />
            <span className="font-display text-2xl">Wingwomen</span>
          </Link>
          <div>
            <div className="text-xs tracking-[0.25em] uppercase text-coral-700 mb-4"><span className="rule bg-coral opacity-100" />Private by design</div>
            <h2 className="font-display text-4xl leading-tight max-w-md">
              You'll never appear by your real name. Only your journey.
            </h2>
          </div>
        </div>
      </div>
      <div className="flex items-center justify-center p-8">
        <form onSubmit={submit} className="w-full max-w-md space-y-6" data-testid="auth-form">
          <div>
            <div className="text-xs tracking-[0.25em] uppercase text-sage-600 mb-3"><span className="rule" />
              {mode === "login" ? "Welcome back" : "Join the circle"}
            </div>
            <h1 className="font-display text-4xl leading-tight">
              {mode === "login" ? "Sign in to your wingwomen." : "Create your anonymous profile."}
            </h1>
          </div>

          <div className="space-y-4">
            <div>
              <label className="text-xs tracking-[0.15em] uppercase text-muted-foreground">Email</label>
              <input
                type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
                data-testid="auth-email"
                className="mt-1 w-full bg-white border border-border rounded-lg px-4 py-3 focus:border-coral focus:ring-2 focus:ring-coral/20 outline-none transition-colors"
                placeholder="you@example.com"
              />
            </div>
            <div>
              <label className="text-xs tracking-[0.15em] uppercase text-muted-foreground">Password</label>
              <input
                type="password" required minLength={6} value={password} onChange={(e) => setPassword(e.target.value)}
                data-testid="auth-password"
                className="mt-1 w-full bg-white border border-border rounded-lg px-4 py-3 focus:border-coral focus:ring-2 focus:ring-coral/20 outline-none transition-colors"
                placeholder="At least 6 characters"
              />
            </div>
          </div>

          <button
            type="submit" disabled={busy || (mode === "register" && (!agreed || !adult))}
            data-testid="auth-submit"
            className="w-full flex items-center justify-center gap-2 bg-coral text-white py-3.5 rounded-full font-medium shadow-card hover:bg-coral-600 disabled:opacity-60 disabled:cursor-not-allowed transition-colors"
          >
            {busy ? "Please wait…" : (mode === "login" ? "Sign in" : "Create account")}
            {!busy && <ArrowRight size={18} />}
          </button>

          <div className="flex items-center gap-3">
            <div className="flex-1 h-px bg-border" />
            <span className="text-xs tracking-[0.2em] uppercase text-muted-foreground">or</span>
            <div className="flex-1 h-px bg-border" />
          </div>

          <button
            type="button"
            onClick={() => {
              // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
              const redirectUrl = window.location.origin + "/auth";
              window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
            }}
            data-testid="google-signin"
            className="w-full flex items-center justify-center gap-2.5 bg-white border border-border py-3.5 rounded-full font-medium text-ink hover:border-ink transition-colors"
          >
            <GoogleLogo size={20} weight="bold" className="text-coral-700" />
            Continue with Google
          </button>

          {mode === "register" && (
            <div className="space-y-2.5">
              <label className="flex items-start gap-2.5 text-xs text-ink/70 leading-relaxed cursor-pointer" data-testid="age-label">
                <input
                  type="checkbox" checked={adult} onChange={(e) => setAdult(e.target.checked)}
                  data-testid="age-checkbox"
                  className="mt-0.5 accent-coral w-4 h-4 shrink-0"
                />
                <span>I confirm I am <strong>18 or older</strong> (or the age of majority in my jurisdiction).</span>
              </label>
              <label className="flex items-start gap-2.5 text-xs text-ink/70 leading-relaxed cursor-pointer" data-testid="tos-label">
                <input
                  type="checkbox" checked={agreed} onChange={(e) => setAgreed(e.target.checked)}
                  data-testid="tos-checkbox"
                  className="mt-0.5 accent-coral w-4 h-4 shrink-0"
                />
                <span>
                  I agree to the{" "}
                  <Link to="/terms" target="_blank" data-testid="tos-link" className="text-coral-700 underline underline-offset-4 hover:text-coral-600">
                    Terms of Service
                  </Link>{" "}
                  and{" "}
                  <Link to="/terms#medical" target="_blank" data-testid="med-link" className="text-coral-700 underline underline-offset-4 hover:text-coral-600">
                    Medical Disclaimer
                  </Link>. I understand Wingwomen is a peer-support platform, not a medical
                  provider, and no content on the platform is medical advice.
                </span>
              </label>
            </div>
          )}

          <div className="text-sm text-muted-foreground text-center">
            {mode === "login" ? "New here?" : "Already have an account?"}{" "}
            <button type="button" onClick={() => setMode(mode === "login" ? "register" : "login")}
                    data-testid="auth-toggle"
                    className="text-coral-700 underline underline-offset-4 hover:text-coral-600">
              {mode === "login" ? "Create an account" : "Sign in"}
            </button>
          </div>
          <p className="text-xs text-center text-muted-foreground leading-relaxed">
            By continuing you agree to our community rules. Not medical advice.
          </p>
        </form>
      </div>
    </div>
  );
}
